import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  fontSizes,
  radii,
  spacing,
} from "@softmax/design-system/tokens.stylex";

type PlayerImagePickerProps = Readonly<{
  id: string;
  imageName: string | null;
  previewUrl: string | null;
  requirementsId: string | undefined;
  onImageChange(image: File | null): void;
}>;

export function PlayerImagePicker({
  id,
  imageName,
  previewUrl,
  requirementsId,
  onImageChange,
}: PlayerImagePickerProps) {
  return (
    <label htmlFor={id} {...stylex.props(styles.imagePicker)}>
      {previewUrl ? (
        <img src={previewUrl} alt="" {...stylex.props(styles.preview)} />
      ) : null}
      <span {...stylex.props(styles.filename)}>
        {imageName ?? "Add a picture (optional)"}
      </span>
      <input
        id={id}
        name="player-image"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        aria-describedby={requirementsId}
        {...stylex.props(styles.hidden)}
        onChange={(event) => onImageChange(event.target.files?.[0] ?? null)}
      />
    </label>
  );
}

const styles = stylex.create({
  hidden: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    borderWidth: 0,
  },
  imagePicker: {
    display: "flex",
    minHeight: 44,
    flexShrink: 0,
    alignItems: "center",
    gap: spacing.space8,
    borderRadius: radii.small,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.border,
    paddingInline: spacing.space12,
    fontFamily: fonts.sans,
    fontSize: fontSizes.reading,
    lineHeight: "22px",
    color: colors.muted,
    cursor: "pointer",
    outline: {
      default: "none",
      ":focus-within": `2px solid ${colors.foreground}`,
    },
    outlineOffset: 2,
  },
  preview: {
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: radii.pill,
    objectFit: "cover",
  },
  filename: {
    maxWidth: 120,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});
